'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

// Simple RTL detection function to avoid external library issues during SSR
function getLanguageDirection(locale: string): 'ltr' | 'rtl' {
  // RTL languages
  const rtlLanguages = ['ar', 'he', 'fa', 'ur', 'yi', 'ji', 'iw', 'ku', 'ps', 'sd', 'ug'];
  const language = locale.split('-')[0].toLowerCase();
  return rtlLanguages.includes(language) ? 'rtl' : 'ltr';
}

// Types
export type Direction = 'ltr' | 'rtl';

interface RTLContextValue {
  /** Current text direction */
  direction: Direction;
  /** Whether the current direction is RTL */
  isRTL: boolean;
  /** Current locale */
  locale: string;
  /** Override direction (for testing) */
  setDirection: (direction: Direction) => void;
  /** Reset to automatic detection */
  resetDirection: () => void;
}

interface RTLProviderProps {
  /** Current locale */
  locale: string;
  /** Initial direction (optional, will auto-detect if not provided) */
  initialDirection?: Direction;
  /** Whether to allow manual override */
  allowOverride?: boolean;
  /** Children to render */
  children: React.ReactNode;
}

// Context
const RTLContext = createContext<RTLContextValue | undefined>(undefined);

// Provider Component
export function RTLProvider({
  locale,
  initialDirection,
  allowOverride = true,
  children,
}: RTLProviderProps) {
  // Auto-detect direction from locale if not provided
  const detectedDirection = initialDirection || getLanguageDirection(locale);
  const [direction, setDirectionState] = useState<Direction>(detectedDirection);
  const [isManualOverride, setIsManualOverride] = useState(false);

  // Update direction when locale changes (if not manually overridden)
  useEffect(() => {
    if (!isManualOverride) {
      const newDirection = getLanguageDirection(locale);
      setDirectionState(newDirection);
    }
  }, [locale, isManualOverride]);

  // Apply direction to document - only update data-direction attribute
  // Note: The dir attribute is already set in the HTML tag by the layout
  useEffect(() => {
    if (typeof document !== 'undefined') {
      // Only sync if manually overridden, otherwise let SSR value persist
      if (isManualOverride && document.documentElement.dir !== direction) {
        document.documentElement.dir = direction;
      }
      // Always update data-direction for CSS purposes
      document.documentElement.setAttribute('data-direction', direction);
    }
  }, [direction, isManualOverride]);

  const setDirection = (newDirection: Direction) => {
    if (allowOverride) {
      setDirectionState(newDirection);
      setIsManualOverride(true);
    }
  };

  const resetDirection = () => {
    const autoDirection = getLanguageDirection(locale);
    setDirectionState(autoDirection);
    setIsManualOverride(false);
  };

  const value: RTLContextValue = {
    direction,
    isRTL: direction === 'rtl',
    locale,
    setDirection,
    resetDirection,
  };

  return <RTLContext.Provider value={value}>{children}</RTLContext.Provider>;
}

// Default SSR-safe context value
const defaultContextValue: RTLContextValue = {
  direction: 'ltr',
  isRTL: false,
  locale: 'en',
  setDirection: () => {},
  resetDirection: () => {},
};

// Hooks
export function useRTL() {
  const context = useContext(RTLContext);
  // Return default value during SSR instead of throwing
  if (!context) {
    return defaultContextValue;
  }
  return context;
}

export function useDirection(): Direction {
  const { direction } = useRTL();
  return direction;
}

export function useIsRTL(): boolean {
  const { isRTL } = useRTL();
  return isRTL;
}

// Utility hook for directional values
export function useDirectionalValue<T>(ltrValue: T, rtlValue: T): T {
  const isRTL = useIsRTL();
  return isRTL ? rtlValue : ltrValue;
}

// Utility hook for directional styles
export function useDirectionalStyles(
  ltrStyles: React.CSSProperties,
  rtlStyles: React.CSSProperties
): React.CSSProperties {
  const isRTL = useIsRTL();
  return isRTL ? rtlStyles : ltrStyles;
}

// HOC for RTL-aware components
export function withRTL<P extends object>(
  Component: React.ComponentType<P & { isRTL: boolean; direction: Direction }>
) {
  return function RTLComponent(props: P) {
    const { isRTL, direction } = useRTL();
    return <Component {...props} isRTL={isRTL} direction={direction} />;
  };
}

// Utility component for conditional rendering based on direction
interface DirectionalProps {
  children: React.ReactNode;
  showIn?: Direction | 'both';
}

export function Directional({ children, showIn = 'both' }: DirectionalProps) {
  const direction = useDirection();

  if (showIn === 'both') {
    return <>{children}</>;
  }

  if (showIn === direction) {
    return <>{children}</>;
  }

  return null;
}

// Export everything
export type { RTLProviderProps, RTLContextValue };
